import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageName from "../../components/PageName";
import Card from "../../components/Base/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useAppSelector } from "../../redux/hooks";
import selfServicesService from "../../redux/selfService/selfServicesService";
import { RouteNames } from "../../constants";
import InactivityTimeoutHOC from "../auth/inactivity-timeout-hoc";

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
};

const RecaptureStatusPage = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<any>(null);

  useEffect(() => {
    if (user?.isRecaptured) return;
    const load = async () => {
      setLoading(true);
      try {
        const response = await selfServicesService.getRecaptureDetails();
        const data = response?.data?.responseObject;
        if (!data) {
          toast.error(response?.data?.statusMessage || "Unable to load recapture status.");
          return;
        }
        setDetails(data);
      } catch (err: any) {
        toast.error(err?.response?.data?.statusMessage || "Unable to load recapture status.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.isRecaptured]);

  if (user?.isRecaptured) {
    return <Navigate to={RouteNames.HOME} replace />;
  }

  const status = details?.statusDisplay || details?.recaptureStatus || "Not started";
  const canEdit = !!details?.canEdit;

  return (
    <div className="transaction-page settings-page">
      <PageName pageName="Recapture Status" />
      <Card className="transaction-card">
        <div className="h1 text-grayscaleBody border-b pb-4 mb-4">Recapture application status</div>
        {loading ? (
          <LoadingSpinner size="md" />
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-4">
              <p className="text-sm text-slate-500 mb-1">Current status</p>
              <p className="text-xl font-semibold text-grayscaleHeader">{status}</p>
              {details?.statusMessage && (
                <p className="text-sm text-grayscaleBody mt-2">{details.statusMessage}</p>
              )}
            </div>

            <div className="form__row">
              <div className="form__group">
                <label className="form__label">PIN</label>
                <div className="form__input bg-slate-50">{details?.pin || "-"}</div>
              </div>
              <div className="form__group">
                <label className="form__label">Submitted / last saved</label>
                <div className="form__input bg-slate-50">
                  {formatDate(details?.recaptureDate || details?.lastUpdated)}
                </div>
              </div>
            </div>

            <div className="form__row">
              <div className="form__group">
                <label className="form__label">Photo on file</label>
                <div className="form__input bg-slate-50">{details?.hasPicture ? "Yes" : "No"}</div>
              </div>
              <div className="form__group">
                <label className="form__label">Signature on file</label>
                <div className="form__input bg-slate-50">{details?.hasSignature ? "Yes" : "No"}</div>
              </div>
            </div>

            <div className="form__group">
              <label className="form__label">Consent form on file</label>
              <div className="form__input bg-slate-50">{details?.hasConsentForm ? "Yes" : "No"}</div>
            </div>

            {details?.employmentRecord?.employerName && (
              <div className="form__group">
                <label className="form__label">Employer</label>
                <div className="form__input bg-slate-50">
                  {details.employmentRecord.employerName}
                  {details.employmentRecord.employerBranchName
                    ? ` - ${details.employmentRecord.employerBranchName}`
                    : ""}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-end pt-2">
              {canEdit && (
                <Link to={RouteNames.RECAPTURE} className="btn btn-primary">
                  {details?.isDraft ? "Continue draft" : "Edit recapture"}
                </Link>
              )}
              {!canEdit && !details?.isRecaptured && (
                <p className="text-sm text-slate-500 self-center">
                  Editing is locked while your application is under review.
                </p>
              )}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default InactivityTimeoutHOC(RecaptureStatusPage, 900000);
